import React, { useEffect, useState } from 'react';
import { Terminal, Shield, Lock, Code } from 'lucide-react';
export function LoadingScreen() {
  const [progress, setProgress] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const loadingTexts = ['> Initializing security protocols...', '> Loading encryption modules...', '> Establishing secure connection...', '> Verifying authentication tokens...', '> Loading cybersecurity framework...', '> System ready.'];
  useEffect(() => {
    // Progress animation
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + 2;
      });
    }, 30);
    // Text animation
    let textIndex = 0;
    const textInterval = setInterval(() => {
      if (textIndex < loadingTexts.length) {
        setCurrentText(loadingTexts[textIndex]);
        textIndex++;
      } else {
        clearInterval(textInterval);
      }
    }, 500);
    return () => {
      clearInterval(progressInterval);
      clearInterval(textInterval);
    };
  }, []);
  return <div className="fixed inset-0 z-[99999] bg-slate-950 flex items-center justify-center overflow-hidden" data-id="element-340">
      {/* Matrix-style background */}
      <div className="absolute inset-0 opacity-10" data-id="element-341">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#0f172a_1px,transparent_1px),linear-gradient(to_bottom,#0f172a_1px,transparent_1px)] bg-[size:4rem_4rem]" data-id="element-342"></div>
      </div>

      {/* Cyber glow effects */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" data-id="element-343"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl animate-pulse animation-delay-300" data-id="element-344"></div>

      {/* Scanline effect */}
      <div className="absolute inset-0 bg-[linear-gradient(to_bottom,transparent_50%,rgba(6,182,212,0.03)_50%)] bg-[length:100%_4px] pointer-events-none" data-id="element-345"></div>

      {/* Main loading content */}
      <div className="relative z-10 max-w-2xl w-full px-8" data-id="element-346">
        {/* Logo and Shield */}
        <div className="flex justify-center mb-8 animate-fade-in-up" data-id="element-347">
          <div className="relative" data-id="element-348">
            <Shield className="h-20 w-20 text-cyan-500 animate-pulse drop-shadow-[0_0_20px_rgba(6,182,212,0.8)]" data-id="element-349" />
            <div className="absolute inset-0 bg-cyan-500/30 blur-2xl animate-pulse" data-id="element-350"></div>

            {/* Rotating ring */}
            <div className="absolute inset-0 -m-4" data-id="element-351">
              <div className="w-28 h-28 border-2 border-cyan-500/30 rounded-full animate-spin-slow" data-id="element-352"></div>
            </div>
            <div className="absolute inset-0 -m-6" data-id="element-353">
              <div className="w-32 h-32 border border-cyan-500/20 rounded-full animate-spin-slower" data-id="element-354"></div>
            </div>
          </div>
        </div>

        {/* Brand name */}
        <div className="text-center mb-8 animate-fade-in-up animation-delay-100" data-id="element-355">
          <h1 className="text-3xl font-bold text-slate-100 font-mono mb-2" data-id="element-356">
            CyberSecurity<span className="text-cyan-500" data-id="element-357">Protocol</span>
          </h1>
          <p className="text-sm text-slate-500 font-mono" data-id="element-358">
            v1.0.1 | Build 2024
          </p>
        </div>

        {/* Terminal window */}
        <div className="bg-slate-900/80 backdrop-blur-xl border border-cyan-500/20 rounded-xl p-6 mb-6 animate-fade-in-up animation-delay-200" data-id="element-359">
          {/* Terminal header */}
          <div className="flex items-center space-x-2 mb-4 pb-3 border-b border-slate-800" data-id="element-360">
            <Terminal className="h-4 w-4 text-cyan-500" data-id="element-361" />
            <span className="text-xs text-slate-400 font-mono" data-id="element-362">system.log</span>
            <div className="flex-1" data-id="element-363"></div>
            <div className="flex space-x-1" data-id="element-364">
              <div className="w-3 h-3 rounded-full bg-red-500/50" data-id="element-365"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-500/50" data-id="element-366"></div>
              <div className="w-3 h-3 rounded-full bg-green-500/50" data-id="element-367"></div>
            </div>
          </div>

          {/* Terminal content */}
          <div className="space-y-2 font-mono text-sm min-h-[120px]" data-id="element-368">
            {loadingTexts.slice(0, loadingTexts.indexOf(currentText) + 1).map((text, index) => <div key={index} className={`flex items-center space-x-2 ${index === loadingTexts.indexOf(currentText) ? 'text-cyan-400' : 'text-slate-500'}`} data-id="element-369">
                  <span className="text-emerald-500" data-id="element-370">$</span>
                  <span className="animate-fade-in" data-id="element-371">{text}</span>
                  {index === loadingTexts.indexOf(currentText) && <span className="animate-pulse text-cyan-400" data-id="element-372">▊</span>}
                </div>)}
          </div>
        </div>

        {/* Progress bar */}
        <div className="animate-fade-in-up animation-delay-300" data-id="element-373">
          <div className="flex items-center justify-between mb-2" data-id="element-374">
            <span className="text-xs text-slate-500 font-mono" data-id="element-375">Loading...</span>
            <span className="text-xs text-cyan-400 font-mono font-bold" data-id="element-376">
              {progress}%
            </span>
          </div>
          <div className="h-2 bg-slate-900 rounded-full overflow-hidden border border-slate-800" data-id="element-377">
            <div className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500 transition-all duration-300 ease-out relative" style={{
            width: `${progress}%`
          }} data-id="element-378">
              <div className="absolute inset-0 bg-white/20 animate-shimmer" data-id="element-379"></div>
            </div>
          </div>
        </div>

        {/* Status indicators */}
        <div className="grid grid-cols-3 gap-4 mt-8 animate-fade-in-up animation-delay-400" data-id="element-380">
          <StatusIndicator icon={<Shield className="h-4 w-4" data-id="element-382" />} label="Security" status="active" data-id="element-381" />
          <StatusIndicator icon={<Lock className="h-4 w-4" data-id="element-384" />} label="Encryption" status="active" data-id="element-383" />
          <StatusIndicator icon={<Code className="h-4 w-4" data-id="element-386" />} label="Protocol" status="active" data-id="element-385" />
        </div>
      </div>
    </div>;
}
interface StatusIndicatorProps {
  icon: React.ReactNode;
  label: string;
  status: 'active' | 'loading' | 'ready';
}
function StatusIndicator({
  icon,
  label,
  status
}: StatusIndicatorProps) {
  return <div className="flex items-center space-x-2 text-xs" data-id="element-387">
      <div className="text-cyan-500 animate-pulse" data-id="element-388">{icon}</div>
      <div data-id="element-389">
        <div className="text-slate-400 font-mono" data-id="element-390">{label}</div>
        <div className="flex items-center space-x-1" data-id="element-391">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" data-id="element-392"></div>
          <span className="text-emerald-500 font-mono text-[10px]" data-id="element-393">
            {status}
          </span>
        </div>
      </div>
    </div>;
}